import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "./UI";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { LineChart as LineIcon, Loader2, TrendingUp, MousePointerClick, UserPlus, Eye } from "lucide-react";

const TIER_LABELS = {
  candidate: "Candidate",
  campaign_pro: "Campaign Pro",
  agency: "Agency",
  unknown: "Unknown",
};

/**
 * PricingAnalyticsCard — admin-only rollup of the pricing funnel over the
 * last 30 days: page views, toggle changes, tier clicks and signup submits.
 * Powered by GET /api/analytics/pricing-summary.
 */
export function PricingAnalyticsCard() {
  const [data, setData] = useState(null);
  const [topRoutes, setTopRoutes] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api
      .get("/analytics/pricing-summary")
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load analytics"));
    api
      .get("/analytics/top-nav-routes?limit=10")
      .then((r) => setTopRoutes(r.data))
      .catch(() => setTopRoutes({ rows: [] }));
  }, []);

  if (err) {
    return (
      <Card data-testid="pricing-analytics-error">
        <div className="text-xs text-rose-300">{err}</div>
      </Card>
    );
  }
  if (!data) {
    return (
      <Card data-testid="pricing-analytics-loading">
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin text-cyan-300" /> Loading pricing analytics…
        </div>
      </Card>
    );
  }

  const c = data.counts || {};
  const views = c.pricing_page_view || 0;
  const tierClicks = c.tier_select_click || 0;
  const modalOpens = c.signup_modal_open || 0;
  const signupSubmits = c.signup_submit || 0;
  const clickThrough = views ? Math.round((tierClicks / views) * 100) : 0;
  const submitRate = modalOpens ? Math.round((signupSubmits / modalOpens) * 100) : 0;

  const tierRows = Object.entries(data.tiers || {}).map(([tier, v]) => ({
    tier: TIER_LABELS[tier] || tier,
    clicks: v.tier_select_click || 0,
    submits: v.signup_submit || 0,
  }));

  return (
    <Card padded={false} data-testid="pricing-analytics-card">
      <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-cyan-300" />
          <h3 className="font-display text-lg font-bold">Pricing funnel (last 30 days)</h3>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-slate-500">Internal — admins only</span>
      </div>
      <div className="p-5 space-y-5">
        {/* KPI strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi icon={Eye} label="Pricing page views" value={views} />
          <Kpi icon={MousePointerClick} label="Tier clicks" value={tierClicks} sub={`${clickThrough}% CTR`} />
          <Kpi icon={UserPlus} label="Signup modals opened" value={modalOpens} />
          <Kpi icon={LineIcon} label="Signup submits" value={signupSubmits} sub={`${submitRate}% of opens`} />
        </div>

        {/* Per-tier breakdown */}
        <div className="grid lg:grid-cols-2 gap-5">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
              Tier breakdown
            </div>
            {tierRows.length === 0 ? (
              <div className="text-xs text-slate-500 py-8 text-center">No tier events yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={tierRows} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--contrast-rgb) / 0.05)" />
                  <XAxis dataKey="tier" stroke="var(--text-muted)" fontSize={11} />
                  <YAxis stroke="var(--text-muted)" fontSize={11} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      background: "var(--bg-elevated)",
                      border: "1px solid rgb(var(--ok-400) / 0.3)",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                  <Bar dataKey="clicks" name="Tier clicks" fill="var(--accent-primary)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="submits" name="Signups" fill="var(--accent-deep)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
              Daily views (14d)
            </div>
            {(data.view_trend || []).length === 0 ? (
              <div className="text-xs text-slate-500 py-8 text-center">No page views yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.view_trend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgb(var(--contrast-rgb) / 0.05)" />
                  <XAxis
                    dataKey="date"
                    stroke="var(--text-muted)"
                    fontSize={10}
                    tickFormatter={(d) => (d || "").slice(5)}
                  />
                  <YAxis stroke="var(--text-muted)" fontSize={11} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      background: "var(--bg-elevated)",
                      border: "1px solid rgb(var(--ok-400) / 0.3)",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                  <Bar dataKey="views" fill="var(--accent-primary)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Nudge conversions */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 pt-2 border-t border-white/5">
          <MiniStat label="Annual nudge shown" value={c.billing_annual_nudge_shown || 0} />
          <MiniStat label="Annual nudge clicked" value={c.billing_annual_nudge_click || 0} />
          <MiniStat label="Verified promo shown" value={c.verified_badge_promo_shown || 0} />
          <MiniStat label="Verified promo clicked" value={c.verified_badge_promo_click || 0} />
          <MiniStat label="Sticky demo shown" value={c.sticky_demo_shown || 0} />
          <MiniStat label="Sticky demo clicked" value={c.sticky_demo_click || 0} />
          <MiniStat label="Walkthroughs opened" value={c.feature_walkthrough_open || 0} />
          <MiniStat label="Walkthroughs completed" value={c.feature_walkthrough_complete || 0} />
        </div>

        {/* A/B experiments */}
        {data.experiments && Object.keys(data.experiments).length > 0 && (
          <div className="pt-2 border-t border-white/5" data-testid="pricing-analytics-experiments">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-medium mb-2">
              A/B experiments
            </div>
            <div className="grid gap-3">
              {Object.entries(data.experiments).map(([exp, variants]) => (
                <div
                  key={exp}
                  className="rounded-xl border border-white/10 bg-white/[0.02] p-4"
                  data-testid={`experiment-${exp}`}
                >
                  <div className="text-sm text-white font-semibold mb-3">{exp}</div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-slate-500">
                        <th className="pb-2 font-medium">Variant</th>
                        <th className="pb-2 font-medium text-right">Exposures</th>
                        <th className="pb-2 font-medium text-right">Clicks</th>
                        <th className="pb-2 font-medium text-right">CTR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(variants).map(([v, counts]) => {
                        const exposures = counts.experiment_exposure || 0;
                        const clicks = counts.experiment_click || 0;
                        const ctr = exposures ? Math.round((clicks / exposures) * 100) : 0;
                        return (
                          <tr key={v} className="border-t border-white/5">
                            <td className="py-1.5 text-slate-200">{v}</td>
                            <td className="py-1.5 text-right font-mono">{exposures}</td>
                            <td className="py-1.5 text-right font-mono">{clicks}</td>
                            <td className="py-1.5 text-right font-mono text-cyan-300">{ctr}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Top nav routes across all users */}
        {topRoutes && (topRoutes.rows || []).length > 0 && (
          <TopRoutesPromotionSection topRoutes={topRoutes} />
        )}
      </div>
    </Card>
  );
}

function TopRoutesPromotionSection({ topRoutes }) {
  const [current, setCurrent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    api
      .get("/app-settings/quick-access-routes")
      .then((r) => setCurrent(r.data))
      .catch(() => setCurrent({ routes: [] }));
  }, []);

  const totalVisits = (topRoutes.rows || []).reduce((s, r) => s + (r.visits || 0), 0);
  const daysCovered = topRoutes.range_days || 0;
  // "Ready to promote" once we have real signal: 30+ days AND ≥50 total visits
  // in the top 10 AND at least 3 rows.
  const readyToPromote = daysCovered >= 30 && totalVisits >= 50 && (topRoutes.rows || []).length >= 3;
  const suggested = (topRoutes.rows || []).slice(0, 3).map((r) => r.path);
  const alreadyPromoted =
    current?.routes &&
    suggested.length === current.routes.length &&
    suggested.every((p, i) => current.routes[i] === p);

  const promote = async () => {
    setBusy(true);
    setNotice(null);
    try {
      await api.post("/app-settings/quick-access-routes", { routes: suggested });
      setCurrent({ routes: suggested, updated_at: new Date().toISOString() });
      setNotice({ kind: "ok", text: "Promoted to header quick-access bar." });
    } catch (e) {
      setNotice({ kind: "err", text: e?.response?.data?.detail || "Could not update setting." });
    } finally {
      setBusy(false);
    }
  };
  const clear = async () => {
    setBusy(true);
    try {
      await api.post("/app-settings/quick-access-routes", { routes: [] });
      setCurrent({ routes: [], updated_at: new Date().toISOString() });
      setNotice({ kind: "ok", text: "Quick-access bar cleared." });
    } catch (e) {
      setNotice({ kind: "err", text: e?.response?.data?.detail || "Could not update setting." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-2 border-t border-white/5" data-testid="pricing-analytics-top-routes">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">
            Top-visited nav routes (last {daysCovered} days)
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5">
            {totalVisits.toLocaleString()} total visits ·{" "}
            {readyToPromote ? (
              <span className="text-emerald-300">enough data to auto-suggest a quick-access bar</span>
            ) : (
              <span>need ≥30 days &amp; ≥50 visits before recommending promotion</span>
            )}
          </div>
        </div>
        {readyToPromote && !alreadyPromoted && (
          <button
            onClick={promote}
            disabled={busy}
            data-testid="promote-quick-access-btn"
            className="text-xs px-3 py-1.5 rounded-full bg-gradient-to-r from-emerald-400 to-teal-500 text-slate-900 font-semibold disabled:opacity-50"
          >
            Promote top 3 to Quick Access Bar
          </button>
        )}
        {alreadyPromoted && (
          <button
            onClick={clear}
            disabled={busy}
            data-testid="clear-quick-access-btn"
            className="text-xs px-3 py-1.5 rounded-full border border-white/20 text-slate-300 hover:text-white"
          >
            Clear quick-access bar
          </button>
        )}
      </div>
      {notice && (
        <div className={`text-xs mb-2 ${notice.kind === "ok" ? "text-emerald-300" : "text-rose-300"}`}>
          {notice.text}
        </div>
      )}
      {current?.routes?.length > 0 && (
        <div className="text-[11px] text-slate-400 mb-2" data-testid="current-quick-access">
          Currently promoted:{" "}
          {current.routes.map((r, i) => (
            <span key={r} className="text-cyan-300">
              {r}
              {i < current.routes.length - 1 ? ", " : ""}
            </span>
          ))}
        </div>
      )}
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-slate-500">
            <th className="pb-2 font-medium">Route</th>
            <th className="pb-2 font-medium">Group</th>
            <th className="pb-2 font-medium text-right">Visits</th>
            <th className="pb-2 font-medium text-right">Unique users</th>
          </tr>
        </thead>
        <tbody>
          {topRoutes.rows.map((r, i) => (
            <tr key={r.path || i} className="border-t border-white/5">
              <td className="py-1.5 text-slate-200">
                <span className="text-slate-400 mr-1">{i + 1}.</span>
                {r.label || r.path}
                {i < 3 && readyToPromote && (
                  <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-400/15 text-emerald-300 border border-emerald-400/30">
                    top 3
                  </span>
                )}
              </td>
              <td className="py-1.5 text-slate-400 text-[11px]">{r.group || "—"}</td>
              <td className="py-1.5 text-right font-mono text-cyan-300">{r.visits}</td>
              <td className="py-1.5 text-right font-mono text-slate-300">{r.unique_visitors}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value, sub }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
    <div className="flex items-center gap-2 text-slate-400">
      <Icon className="w-3.5 h-3.5 text-cyan-300" />
      <div className="text-[10px] uppercase tracking-wider">{label}</div>
    </div>
    <div className="font-display text-3xl font-bold mt-2">{value.toLocaleString()}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

const MiniStat = ({ label, value }) => (
  <div className="text-center">
    <div className="font-display text-xl font-bold">{value.toLocaleString()}</div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{label}</div>
  </div>
);
